/** Calendar layout adapted from Rare UI (MIT), see github-activity.LICENSE. */
export interface ActivityDay { readonly date: string; readonly count: number }
const DAY = 86_400_000;

/** Keep UTC dates aligned to Sunday columns, including blank future cells. */
export function activityWeeks(days: ReadonlyArray<ActivityDay>, to: string, months: 6 | 12) {
  const end = Date.parse(`${to}T00:00:00Z`);
  if (!Number.isFinite(end)) return [];
  const start = end - ((months === 6 ? 182 : 365) - 1) * DAY;
  const first = start - new Date(start).getUTCDay() * DAY;
  const last = end + (6 - new Date(end).getUTCDay()) * DAY;
  const byDate = new Map(days.map((day) => [day.date, day]));
  const weeks: Array<Array<ActivityDay | null>> = [];
  for (let week = first; week <= last; week += DAY * 7) {
    weeks.push(Array.from({ length: 7 }, (_, index) => {
      const at = week + index * DAY;
      if (at < start || at > end) return null;
      // An absent day is unknown, never a fabricated zero.
      return byDate.get(new Date(at).toISOString().slice(0, 10)) ?? null;
    }));
  }
  return weeks;
}

export function activityMonthLabels(weeks: ReadonlyArray<ReadonlyArray<ActivityDay | null>>) {
  let previous = "";
  let lastLabel = -4;
  return weeks.map((week, index) => {
    const first = week.find((day) => day !== null);
    const month = first?.date.slice(0, 7) ?? "";
    if (!month || month === previous) return null;
    previous = month;
    if (index - lastLabel < 3 || weeks.length - index < 2) return null;
    lastLabel = index;
    return new Date(`${month}-01T00:00:00Z`).toLocaleDateString("en", { month: "short", timeZone: "UTC" });
  });
}

export function activityDayLabel(day: ActivityDay) {
  const date = new Date(`${day.date}T00:00:00Z`).toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric", timeZone: "UTC",
  });
  return `${day.count} ${day.count === 1 ? "check" : "checks"} on ${date} (UTC)`;
}

export const activityLevel = (count: number) => Math.min(4, Math.max(0, Math.floor(count)));

const KEY_MOVES: Readonly<Record<string, number>> = { ArrowRight: 7, ArrowLeft: -7, ArrowDown: 1, ArrowUp: -1 };

/** Unknown days occupy calendar space: arrows must not collapse those gaps. */
export function activityFocusDate(weeks: ReadonlyArray<ReadonlyArray<ActivityDay | null>>, currentDate: string, key: string): string | null | undefined {
  const grid = weeks.flat();
  if (key === "Home") return grid.find((day) => day !== null)?.date ?? null;
  if (key === "End") return grid.findLast((day) => day !== null)?.date ?? null;
  const move = KEY_MOVES[key];
  if (move === undefined) return undefined;
  const position = grid.findIndex((day) => day?.date === currentDate);
  if ((key === "ArrowDown" && position % 7 === 6) || (key === "ArrowUp" && position % 7 === 0)) return null;
  return position < 0 ? null : grid[position + move]?.date ?? null;
}
